(() => {
  'use strict';

  if (window.location.pathname.replace(/\/$/, '') !== '/search') return;
  if (window.PaulissenSearchPageRuntimeReady) return;
  window.PaulissenSearchPageRuntimeReady = true;

  const SECTION_SELECTOR = '[data-search-results-section]';
  const SEARCH_ROOT_SELECTOR = '[data-pw-predictive-search]';
  const SEARCH_INPUT_SELECTOR = '[data-pw-search-input]';
  const SEARCH_TYPES = 'product,page,article';
  const MIN_QUERY = 2;
  const INDEX_MIN = 4;
  const FALLBACK_LIMIT = 12;
  const DEBOUNCE = 180;
  const MOBILE_FILTER_MEDIA = window.matchMedia('(max-width: 760px)');

  let liveTimer = null;
  let priceTimer = null;
  let activeController = null;
  let catalogPromise = null;
  let supplementSerial = 0;

  const root = () => document.querySelector(SEARCH_ROOT_SELECTOR);
  const searchInput = () => root()?.querySelector(SEARCH_INPUT_SELECTOR);
  const getSection = () => document.querySelector(SECTION_SELECTOR);
  const norm = (value) => String(value || '').trim().toLowerCase();
  const ident = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  const compactQuery = (value) => !/\s/.test(String(value || '').trim());

  const pathOf = (value) => {
    try {
      return new URL(value, window.location.origin).pathname.replace(/\/$/, '').toLowerCase();
    } catch (_) {
      return String(value || '').split('?')[0].replace(/\/$/, '').toLowerCase();
    }
  };

  const previewThemeId = () => new URLSearchParams(window.location.search).get('preview_theme_id') || '';

  const hideHeaderDropdown = () => {
    const searchRoot = root();
    const results = searchRoot?.querySelector('[data-pw-search-results]');
    const field = searchInput();
    if (results) results.hidden = true;
    if (field) field.setAttribute('aria-expanded', 'false');
  };

  const hasActiveFacetValue = (facet) => {
    if (facet.querySelector('input[type="checkbox"]:checked, input[type="radio"]:checked')) return true;
    return [...facet.querySelectorAll('input[type="number"]')].some((field) => field.value.trim() !== '');
  };

  const configureResponsiveFacets = (section = getSection(), force = false) => {
    const panel = section?.querySelector('.pw-facets__panel');
    if (!panel) return;

    const mode = MOBILE_FILTER_MEDIA.matches ? 'mobile' : 'desktop';
    if (!force && panel.dataset.pwResponsiveMode === mode) return;
    panel.dataset.pwResponsiveMode = mode;

    if (MOBILE_FILTER_MEDIA.matches) {
      panel.open = false;
      section.querySelectorAll('.pw-facet').forEach((facet) => {
        facet.open = hasActiveFacetValue(facet);
      });
      return;
    }

    panel.open = true;
    section.querySelectorAll('.pw-facet').forEach((facet, index) => {
      facet.open = hasActiveFacetValue(facet) || index < 3;
    });
  };

  const configureFacetForm = (form) => {
    if (!(form instanceof HTMLFormElement)) return;

    let typeField = form.querySelector('input[name="type"]');
    if (!typeField) {
      typeField = document.createElement('input');
      typeField.type = 'hidden';
      typeField.name = 'type';
      form.append(typeField);
    }
    typeField.value = SEARCH_TYPES;

    let prefixField = form.querySelector('input[name="options[prefix]"]');
    if (!prefixField) {
      prefixField = document.createElement('input');
      prefixField.type = 'hidden';
      prefixField.name = 'options[prefix]';
      form.append(prefixField);
    }
    prefixField.value = 'last';

    form.querySelectorAll('input[name="filter.v.availability"], input[name="pw_availability"]')
      .forEach((field) => field.remove());
  };

  const buildFacetUrl = (form) => {
    configureFacetForm(form);
    const url = new URL(form.action, window.location.origin);
    const data = new FormData(form);
    data.set('type', SEARCH_TYPES);
    data.set('options[prefix]', 'last');

    for (const [key, value] of data.entries()) {
      if (String(value).trim() !== '') url.searchParams.append(key, value);
    }

    url.searchParams.delete('page');
    url.searchParams.delete('filter.v.availability');
    url.searchParams.delete('pw_availability');
    const preview = previewThemeId();
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url;
  };

  const liveSearchUrl = (query) => {
    const url = new URL(root()?.dataset.searchUrl || '/search', window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', SEARCH_TYPES);
    url.searchParams.set('options[prefix]', 'last');
    const preview = previewThemeId();
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url;
  };

  const updateCount = (section = getSection()) => {
    if (!section) return;
    const productCount = section.querySelectorAll('.pw-search-mixed-grid .pw-product-card').length;
    const contentCount = section.querySelectorAll('.pw-search-mixed-grid .pw-search-content-card').length;
    const total = productCount + contentCount;
    if (!total) return;
    section.dataset.resultCount = String(total);
    const label = section.querySelector('.pw-results-toolbar__count');
    if (label) label.textContent = `${total} ${total === 1 ? 'resultaat' : 'resultaten'}`;
  };

  const renderSection = async (target, { historyMode = 'push', focusResults = false } = {}) => {
    const currentSection = getSection();
    if (!currentSection) {
      window.location.assign(target);
      return null;
    }

    activeController?.abort();
    activeController = new AbortController();

    const cleanUrl = new URL(target, window.location.origin);
    cleanUrl.searchParams.set('type', SEARCH_TYPES);
    cleanUrl.searchParams.set('options[prefix]', 'last');
    cleanUrl.searchParams.delete('filter.v.availability');
    cleanUrl.searchParams.delete('pw_availability');
    cleanUrl.searchParams.delete('section_id');

    const preview = previewThemeId();
    if (preview) cleanUrl.searchParams.set('preview_theme_id', preview);

    const requestUrl = new URL(cleanUrl);
    requestUrl.searchParams.set('section_id', currentSection.dataset.sectionId);

    currentSection.classList.add('is-loading');
    currentSection.setAttribute('aria-busy', 'true');
    const countLabel = currentSection.querySelector('.pw-results-toolbar__count');
    if (countLabel) countLabel.textContent = 'Zoeken…';

    try {
      const response = await fetch(requestUrl, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: activeController.signal
      });
      if (!response.ok) throw new Error(`Zoekresultaten konden niet worden geladen (${response.status}).`);

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const nextSection = parsed.querySelector(SECTION_SELECTOR);
      if (!nextSection) throw new Error('Shopify gaf geen geldige zoekresultatensectie terug.');

      currentSection.replaceWith(nextSection);
      configureResponsiveFacets(nextSection, true);

      if (historyMode === 'push') window.history.pushState({}, '', cleanUrl);
      if (historyMode === 'replace') window.history.replaceState({}, '', cleanUrl);

      if (focusResults) nextSection.querySelector('[data-results-grid]')?.focus({ preventScroll: true });

      document.dispatchEvent(new CustomEvent('paulissen:search:rendered', {
        detail: { section: nextSection, url: cleanUrl.toString() }
      }));
      return nextSection;
    } catch (error) {
      if (error.name === 'AbortError') return null;
      console.warn('[PW search]', error);
      window.location.assign(cleanUrl);
      return null;
    }
  };

  const indexPage = async (base, page) => {
    const url = new URL(base, window.location.origin);
    url.searchParams.set('page', String(page));
    url.searchParams.set('_pw_search_refresh', `${Date.now()}-${page}`);
    const preview = previewThemeId();
    if (preview) url.searchParams.set('preview_theme_id', preview);

    const response = await fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error(`Typenummerindex kon niet worden geladen (${response.status}).`);
    const json = await response.json();
    if (!Array.isArray(json?.products)) throw new Error('Ongeldige typenummerindex.');
    return json;
  };

  const loadCatalog = () => {
    if (catalogPromise) return catalogPromise;
    catalogPromise = (async () => {
      const base = root()?.dataset.searchIndexUrl || '/collections/all?view=pw-search-index';
      const first = await indexPage(base, 1);
      const pages = Math.max(1, Number(first.pages || 1));
      const requests = [];
      for (let page = 2; page <= pages; page += 1) requests.push(indexPage(base, page));
      return [first, ...(await Promise.all(requests))].flatMap((payload) => payload.products || []);
    })().catch((error) => {
      catalogPromise = null;
      throw error;
    });
    return catalogPromise;
  };

  const scoreValues = (values, needle, offset) => {
    let best = Infinity;
    values.forEach((value) => {
      const haystack = ident(value);
      if (!haystack) return;
      if (haystack === needle) best = Math.min(best, offset);
      else if (haystack.startsWith(needle)) best = Math.min(best, offset + 100 + haystack.length - needle.length);
      else {
        const position = haystack.indexOf(needle);
        if (position !== -1) best = Math.min(best, offset + 200 + position);
      }
    });
    return best;
  };

  const indexMatches = (items, query) => {
    const needle = ident(query);
    return items
      .filter((product) => !product.hidden && product.url)
      .map((product) => {
        const score = Math.min(
          scoreValues([...(product.skus || []), ...(product.barcodes || [])], needle, 0),
          scoreValues([product.title], needle, 1000)
        );
        return Number.isFinite(score) ? { ...product, _score: score } : null;
      })
      .filter(Boolean)
      .sort((a, b) => a._score - b._score || String(a.title || '').localeCompare(String(b.title || ''), 'nl'));
  };

  const hasRefinements = () => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('sort_by') || params.has('page')) return true;
    return [...params.keys()].some((key) => key.startsWith('filter.'));
  };

  const fetchStandardCard = async (product) => {
    const url = new URL(product.url, window.location.origin);
    url.searchParams.set('view', 'pw-search-card');
    const preview = previewThemeId();
    if (preview) url.searchParams.set('preview_theme_id', preview);

    const response = await fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'text/html' }
    });
    if (!response.ok) return null;

    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const card = parsed.querySelector('.pw-product-card');
    return card ? document.importNode(card, true) : null;
  };

  const ensureMixedGrid = (section) => {
    let mixed = section.querySelector('.pw-search-mixed-grid');
    if (mixed) return mixed;

    const resultsGrid = section.querySelector('[data-results-grid]');
    if (!resultsGrid) return null;

    const group = document.createElement('div');
    group.className = 'pw-search-results-group';
    mixed = document.createElement('div');
    mixed.className = 'pw-search-mixed-grid pw-product-grid';
    group.append(mixed);
    resultsGrid.replaceChildren(group);
    return mixed;
  };

  const supplementCurrentResults = async () => {
    const section = getSection();
    if (!section) return;

    const query = String(new URLSearchParams(window.location.search).get('q') || '').trim();
    if (!query || !compactQuery(query) || ident(query).length < INDEX_MIN || hasRefinements()) return;

    const run = ++supplementSerial;
    try {
      const catalog = await loadCatalog();
      if (run !== supplementSerial || section !== getSection()) return;

      const matches = indexMatches(catalog, query).slice(0, FALLBACK_LIMIT);
      if (!matches.length) return;

      const existing = new Set(
        [...section.querySelectorAll('.pw-product-card .pw-product-card__media[href]')]
          .map((link) => pathOf(link.href))
      );

      const missing = matches.filter((product) => {
        const path = pathOf(product.url);
        return path && !existing.has(path);
      });
      if (!missing.length) return;

      const cards = (await Promise.all(missing.map(fetchStandardCard))).filter(Boolean);
      if (run !== supplementSerial || section !== getSection() || !cards.length) return;

      const mixed = ensureMixedGrid(section);
      if (!mixed) return;
      cards.forEach((card) => mixed.append(card));
      updateCount(section);
    } catch (error) {
      console.warn('[PW search] Typenummeraanvulling overgeslagen.', error);
    }
  };

  const scheduleLiveSearch = (query) => {
    window.clearTimeout(liveTimer);
    activeController?.abort();
    hideHeaderDropdown();

    if (query.length < MIN_QUERY) {
      const label = getSection()?.querySelector('.pw-results-toolbar__count');
      if (label && query.length) label.textContent = 'Vul minimaal 2 tekens in';
      return;
    }

    liveTimer = window.setTimeout(async () => {
      const field = searchInput();
      if (!field || norm(field.value) !== norm(query)) return;
      await renderSection(liveSearchUrl(query), { historyMode: 'replace', focusResults: false });
    }, DEBOUNCE);
  };

  document.addEventListener('input', (event) => {
    const field = searchInput();
    if (!field || event.target !== field) return;
    event.stopImmediatePropagation();
    event.stopPropagation();
    scheduleLiveSearch(field.value.trim());
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-facets-form]')) return;
    if (!form.closest(SECTION_SELECTOR)) return;
    event.preventDefault();
    renderSection(buildFacetUrl(form), { historyMode: 'push', focusResults: true });
  });

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
    const form = target.closest('[data-facets-form]');
    if (!(form instanceof HTMLFormElement) || !form.closest(SECTION_SELECTOR)) return;

    if (target.type === 'number') {
      window.clearTimeout(priceTimer);
      priceTimer = window.setTimeout(() => form.requestSubmit(), 450);
      return;
    }
    form.requestSubmit();
  });

  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('[data-facet-link], [data-pagination] a');
    if (!(link instanceof HTMLAnchorElement) || !link.closest(SECTION_SELECTOR)) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    renderSection(link.href, {
      historyMode: 'push',
      focusResults: link.closest('[data-pagination]') !== null
    });
  });

  window.addEventListener('popstate', () => {
    if (getSection()) renderSection(window.location.href, { historyMode: 'none', focusResults: false });
  });

  const handleMediaChange = () => configureResponsiveFacets(getSection(), true);
  if (typeof MOBILE_FILTER_MEDIA.addEventListener === 'function') {
    MOBILE_FILTER_MEDIA.addEventListener('change', handleMediaChange);
  } else {
    MOBILE_FILTER_MEDIA.addListener(handleMediaChange);
  }

  document.addEventListener('paulissen:search:rendered', () => supplementCurrentResults());

  const start = () => {
    hideHeaderDropdown();
    configureResponsiveFacets(getSection(), true);
    supplementCurrentResults();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
